/**
 * WAVE 2.1: Application Status Check Page
 * Secured with magic link token-based access.
 * Includes document upload in resubmit flow.
 * Fully localized via provider.applicationStatus.* namespace.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  APPLICATION_STATUS,
  FLAGGABLE_FIELDS,
  ClinicApplicationProvider,
} from '../../context/ClinicApplicationContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Mail, Clock, CheckCircle, XCircle, AlertTriangle,
  ArrowRight, FileText, Sparkles, RefreshCw, ArrowUpCircle, Upload, Send, Eye, ShieldCheck, Link2, X
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import DateField from '../../components/DateField';

const API = process.env.REACT_APP_BACKEND_URL;

// Status display: backend enum → { icon, colors, i18n label key }
const STATUS_META = {
  submitted:            { icon: Clock,        color: 'text-amber-500',   bgColor: 'bg-amber-100',  labelKey: 'provider.applicationStatus.statusSubmitted',      badgeClass: 'bg-amber-500' },
  pending:              { icon: Clock,        color: 'text-amber-500',   bgColor: 'bg-amber-100',  labelKey: 'provider.applicationStatus.statusSubmitted',      badgeClass: 'bg-amber-500' },
  under_review:         { icon: Eye,          color: 'text-indigo-500',  bgColor: 'bg-indigo-100', labelKey: 'provider.applicationStatus.statusUnderReview',    badgeClass: 'bg-indigo-500' },
  approved:             { icon: CheckCircle,  color: 'text-green-500',   bgColor: 'bg-green-100',  labelKey: 'provider.applicationStatus.statusApproved',       badgeClass: 'bg-green-500' },
  rejected:             { icon: XCircle,      color: 'text-red-500',     bgColor: 'bg-red-100',    labelKey: 'provider.applicationStatus.statusNotApproved',    badgeClass: 'bg-red-500' },
  info_requested:       { icon: AlertTriangle,color: 'text-blue-500',    bgColor: 'bg-blue-100',   labelKey: 'provider.applicationStatus.statusActionRequired', badgeClass: 'bg-blue-500' },
  more_info_requested:  { icon: AlertTriangle,color: 'text-blue-500',    bgColor: 'bg-blue-100',   labelKey: 'provider.applicationStatus.statusActionRequired', badgeClass: 'bg-blue-500' },
  resubmitted:          { icon: ArrowUpCircle,color: 'text-purple-500',  bgColor: 'bg-purple-100', labelKey: 'provider.applicationStatus.statusResubmitted',    badgeClass: 'bg-purple-500' },
  reapplied:            { icon: ArrowUpCircle,color: 'text-purple-500',  bgColor: 'bg-purple-100', labelKey: 'provider.applicationStatus.statusResubmitted',    badgeClass: 'bg-purple-500' },
};
const getStatusMeta = (status) => STATUS_META[status] || { icon: Clock, color: 'text-gray-500', bgColor: 'bg-gray-100', labelKey: 'provider.applicationStatus.statusUnknown', badgeClass: 'bg-gray-500' };

const REQ_STATUS_KEYS = {
  approved: 'provider.applicationStatus.reqVerified',
  verified: 'provider.applicationStatus.reqVerified',
  rejected: 'provider.applicationStatus.reqNotVerified',
  pending_review: 'provider.applicationStatus.reqPending',
  uploaded: 'provider.applicationStatus.reqUploadedNotSubmitted',
  pending:  'provider.applicationStatus.reqPending',
};

const ApplicationStatusCheckContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [application, setApplication] = useState(null);
  const [phase, setPhase] = useState('email');
  const [isLoading, setIsLoading] = useState(false);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [resubmitNote, setResubmitNote] = useState('');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [magicLinkUrl, setMagicLinkUrl] = useState(null);
  // TREATX-STATUS-PORTAL-01 — cross-device poll credentials (memory only).
  const pollRef = useRef({ request_id: null, browser_secret: null, timer: null, inFlight: false, stopped: false, attempts: 0 });

  // Locale-agnostic override of FLAGGABLE_FIELDS.label (see RejectionStatusPage).
  const fieldLabel = (fieldKey) => {
    const key = `provider.rejectionStatus.field_${fieldKey}`;
    const localized = t(key);
    if (localized && localized !== key) return localized;
    return FLAGGABLE_FIELDS[fieldKey]?.label || fieldKey;
  };

  const parseApplication = useCallback((app) => ({
    id: app.application_id,
    clinicName: app.clinic_name || app.contact_name || t('provider.applicationStatus.providerFallback'),
    email: app.email,
    status: app.status,
    submittedAt: app.submitted_at,
    rejectionReason: app.rejection_reason,
    flaggedFields: app.flagged_fields,
    additionalInfoRequest: app.requested_info_note || app.additional_info_request,
    requestedAt: app.requested_at,
    resubmittedAt: app.resubmitted_at,
    reapplyCount: app.reapply_count,
    changedFields: app.changed_fields,
    notes: app.notes,
    verificationDocuments: app.verification_documents || [],
    requirements: app.requirements || [],
    unmappedDocuments: app.unmapped_documents || [],
  }), [t]);

  const fetchStatus = useCallback(async () => {
    // Cookie-authorized read (status_session). No token in URL/query.
    try {
      const res = await api.get('/provider/applications/status');
      const data = res.data;
      if (data?.found && data.application) {
        setApplication(parseApplication(data.application));
        setPhase('authenticated');
        return true;
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      if (detail === 'TOKEN_EXPIRED') {
        toast({ title: t('provider.applicationStatus.linkExpired'), description: t('provider.applicationStatus.linkExpiredDesc'), variant: 'destructive' });
      }
    }
    return false;
  }, [parseApplication, t]);

  const stopPolling = useCallback(() => {
    pollRef.current.stopped = true;
    if (pollRef.current.timer) { clearTimeout(pollRef.current.timer); pollRef.current.timer = null; }
  }, []);

  const pollOnce = useCallback(async () => {
    const p = pollRef.current;
    if (p.stopped || p.inFlight || !p.request_id || document.hidden) return;
    p.inFlight = true;
    try {
      const res = await api.post('/provider/applications/access-ready', {
        request_id: p.request_id, browser_secret: p.browser_secret,
      });
      const status = res.data?.status;
      if (status === 'authenticated') {
        stopPolling();
        await fetchStatus();
        return;
      }
      if (status === 'expired') {
        stopPolling();
        toast({ title: t('provider.applicationStatus.linkExpired'), description: t('provider.applicationStatus.linkExpiredDesc'), variant: 'destructive' });
        return;
      }
    } catch (_e) { /* keep polling on transient errors */ }
    finally { p.inFlight = false; }
    // reschedule with modest backoff after the first ~1 min
    p.attempts += 1;
    const delay = p.attempts < 13 ? 4500 : 9000;
    if (!p.stopped) p.timer = setTimeout(pollOnce, delay);
  }, [fetchStatus, stopPolling, t]);

  const startPolling = useCallback((request_id, browser_secret) => {
    stopPolling();
    pollRef.current = { request_id, browser_secret, timer: null, inFlight: false, stopped: false, attempts: 0 };
    pollRef.current.timer = setTimeout(pollOnce, 3000);
  }, [pollOnce, stopPolling]);

  // Poll immediately when the tab regains focus/visibility; cleanup on unmount.
  useEffect(() => {
    const onVis = () => { if (!document.hidden) pollOnce(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
      stopPolling();
    };
  }, [pollOnce, stopPolling]);

  // ?token= present → deliberate POST exchange (scanner-safe), strip token
  // from URL/history, then load via the cookie session. Same-device flow.
  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (!urlToken) return;
    setIsLoading(true);
    (async () => {
      try {
        const res = await api.post('/provider/applications/status-session/exchange', { token: urlToken });
        if (res.data?.found) {
          try { window.history.replaceState({}, '', window.location.href.replace(/([?&])token=[^&]*/,'$1').replace(/[?&]$/,'')); } catch (_e) { /* noop */ }
          await fetchStatus();
        } else {
          toast({ title: t('provider.applicationStatus.invalidLink'), description: t('provider.applicationStatus.invalidLinkDesc'), variant: 'destructive' });
        }
      } catch (e) {
        const detail = e?.response?.data?.detail?.code || e?.response?.data?.detail || '';
        if (detail === 'TOKEN_EXPIRED') toast({ title: t('provider.applicationStatus.linkExpired'), description: t('provider.applicationStatus.linkExpiredDesc'), variant: 'destructive' });
        else toast({ title: t('provider.applicationStatus.invalidLink'), description: t('provider.applicationStatus.invalidLinkDesc'), variant: 'destructive' });
      } finally { setIsLoading(false); }
    })();
  }, [searchParams, fetchStatus, t]);

  const handleRequestAccess = async () => {
    if (!email.trim()) {
      toast({ title: t('provider.applicationStatus.emailRequired'), description: t('provider.applicationStatus.emailRequiredDesc'), variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    try {
      const res = await api.post('/provider/applications/request-access', { email: email.trim() });
      const data = res.data || {};
      if (data.success) {
        setPhase('link_sent');
        if (data.magic_link) setMagicLinkUrl(data.magic_link);
        if (data.request_id && data.browser_secret) startPolling(data.request_id, data.browser_secret);
        toast({ title: t('provider.applicationStatus.secureLinkSent'), description: t('provider.applicationStatus.secureLinkSentDesc') });
      }
    } catch {
      toast({ title: t('provider.applicationStatus.errorTitle'), description: t('provider.applicationStatus.errorSendLink'), variant: 'destructive' });
    }
    setIsLoading(false);
  };

  const handleResend = () => {
    // Re-issue the secure link for the same email and restart cross-device polling.
    handleRequestAccess();
  };

  // P0-PROVIDER-ONBOARDING-RECOVERY-01 (Failure B) — a LEGITIMATE loss of the
  // application-bound portal session must be handled HERE, by the surface that
  // owns it: tell the provider their secure link expired and send them back to
  // the email step to request a new one. It must never be escalated into a
  // platform-wide logout (there is no account session to log out of).
  const handlePortalSessionLoss = useCallback(() => {
    setShowUploadForm(false);
    setUploadFiles([]);
    setApplication(null);
    setPhase('email');
    toast({
      title: t('provider.applicationStatus.portalSessionEnded'),
      description: t('provider.applicationStatus.portalSessionEndedDesc'),
      variant: 'destructive',
    });
  }, [t]);

  const handleFileUpload = async () => {
    if (!uploadFiles.length || !application?.id) return;
    for (const f of uploadFiles) {
      if (f._needsExpiry && !f._expiryDate) {
        toast({ title: t('provider.applicationStatus.expiryDateRequired'), description: t('provider.applicationStatus.expiryDateRequiredDesc', { name: f.name }), variant: 'destructive' });
        return;
      }
    }
    setIsUploading(true);
    try {
      let uploadedCount = 0;
      const errors = [];
      let sessionLost = false;
      for (const file of uploadFiles) {
        if (!file._docTypeId) {
          errors.push({ name: file.name, error: t('provider.applicationStatus.noReqTarget') });
          continue;
        }
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const res = await api.post(`/provider/applications/${application.id}/documents/upload`, {
          document_type_id: file._docTypeId,
          filename: file.name,
          file_data: base64,
          expiry_date: file._expiryDate || null,
        }).then(r => ({ ok: true, json: async () => r.data, status: 200 }))
          .catch(err => ({ ok: false, status: err?.response?.status || 0, json: async () => (err?.response?.data || {}) }));
        const data = await res.json();
        if (res.ok && data.success) uploadedCount += 1;
        else if (res.status === 401 || res.status === 403) { sessionLost = true; break; }
        else errors.push({ name: file.name, error: (data.detail?.code || data.detail || data.message || `HTTP ${res.status}`) });
      }
      if (uploadedCount > 0) {
        toast({ title: t('provider.applicationStatus.uploaded'), description: t('provider.applicationStatus.uploadedDesc', { n: uploadedCount }) });
        setUploadFiles([]);
        await fetchStatus();
      }
      if (sessionLost) {
        handlePortalSessionLoss();
        return;
      }
      if (errors.length) {
        toast({
          title: uploadedCount > 0 ? t('provider.applicationStatus.partialUpload') : t('provider.applicationStatus.uploadFailed'),
          description: errors.map(e => `${e.name}: ${e.error}`).join(' | '),
          variant: 'destructive',
        });
      }
    } catch (e) {
      console.error('Upload error:', e);
      toast({ title: t('provider.applicationStatus.errorTitle'), description: t('provider.applicationStatus.uploadError'), variant: 'destructive' });
    }
    setIsUploading(false);
  };

  const handleResubmit = async () => {
    if (!application?.id) return;
    setIsResubmitting(true);
    try {
      const res = await api.post(`/provider/applications/${application.id}/resubmit`, {
        email: application.email, note: resubmitNote || t('provider.applicationStatus.defaultResubmitNote'),
      }).then(r => ({ status: 200, data: r.data }))
        .catch(err => ({ status: err?.response?.status || 0, data: err?.response?.data || {} }));
      const data = res.data;
      if (data.success) {
        setShowUploadForm(false);
        setResubmitNote('');
        toast({ title: t('provider.applicationStatus.resubmittedToast'), description: t('provider.applicationStatus.resubmittedToastDesc') });
        await fetchStatus();
      } else if (res.status === 401 || res.status === 403) {
        // FINAL-UAT §9 — same session-loss handling as the upload path: a lost
        // portal session is recovered with a new secure link, never by a
        // platform logout and never as a silent "network error".
        handlePortalSessionLoss();
      } else {
        // Prefer the server's specific explanation (409 / 422 / 502) over a
        // generic message — `detail` may be a string or a structured object.
        const detail = data.detail;
        const serverMessage = (typeof detail === 'string' && detail)
          || detail?.message || detail?.code || data.message || '';
        toast({
          title: t('provider.applicationStatus.errorTitle'),
          description: serverMessage || t('provider.applicationStatus.resubmitFailed'),
          variant: 'destructive',
        });
      }
    } catch {
      toast({ title: t('provider.applicationStatus.errorTitle'), description: t('provider.applicationStatus.networkError'), variant: 'destructive' });
    }
    setIsResubmitting(false);
  };

  // TREATX Doc-Review UAT — a rejected requirement itself is sufficient to
  // expose the replacement upload action for THAT requirement, independent
  // of the application-level info_requested state (backend keeps them
  // consistent, but the UI must not depend solely on it).
  const hasRejectedRequirement = (application?.requirements || []).some(
    (r) => (r.requirement_status || r.status) === 'rejected'
  );
  const canReplaceDocuments = !!application && (
    application.status === 'info_requested' ||
    application.status === 'more_info_requested' ||
    hasRejectedRequirement
  );

  const handleContinue = () => {
    if (application?.status === 'approved') {
      navigate('/login', { state: { email: application.email, message: t('provider.applicationStatus.approvedLoginMsg') } });
    } else if (canReplaceDocuments) {
      setShowUploadForm(true);
    }
  };

  const handleReapply = () => {
    if (application?.status === 'rejected') {
      navigate(`/clinic/rejection-status?id=${application.id}`);
    }
  };

  if (isLoading && phase === 'email') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-blue-600 font-semibold mb-2">
            <Sparkles className="h-5 w-5" />
            {t('provider.applicationStatus.brandLine')}
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{t('provider.applicationStatus.pageTitle')}</h1>
          <p className="text-gray-600 mt-2">
            {phase === 'email' && t('provider.applicationStatus.subEmail')}
            {phase === 'link_sent' && t('provider.applicationStatus.subLinkSent')}
            {phase === 'authenticated' && t('provider.applicationStatus.subAuthenticated')}
          </p>
        </div>

        {/* Phase 1: Email */}
        {phase === 'email' && (
          <Card className="shadow-xl mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4 text-sm text-blue-700 bg-blue-50 rounded-lg p-3">
                <ShieldCheck className="h-4 w-4 flex-shrink-0" />
                <span>{t('provider.applicationStatus.securityBanner')}</span>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('provider.applicationStatus.emailLabel')}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="email"
                      placeholder={t('provider.applicationStatus.emailPlaceholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRequestAccess()}
                      className="pl-10"
                      data-testid="status-email-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={handleRequestAccess}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={isLoading}
                  data-testid="request-access-btn"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2"><RefreshCw className="h-4 w-4 animate-spin" /> {t('provider.applicationStatus.sending')}</span>
                  ) : (
                    <span className="flex items-center gap-2"><Link2 className="h-4 w-4" /> {t('provider.applicationStatus.sendSecureLink')}</span>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Phase 2: Link Sent */}
        {phase === 'link_sent' && (
          <Card className="shadow-xl mb-6">
            <CardContent className="p-6 text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Mail className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{t('provider.applicationStatus.checkYourEmail')}</h3>
              <p className="text-gray-600">
                {/* i18next Trans-style interpolation via dangerouslySetInnerHTML would work,
                    but the safer path is to inject the email inline via key with {{email}}. */}
                <span dangerouslySetInnerHTML={{
                  __html: t('provider.applicationStatus.linkSentBody', { email: `<strong>${email}</strong>` })
                }} />
              </p>
              {magicLinkUrl && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-left" data-testid="mock-magic-link">
                  <p className="text-xs font-medium text-amber-700 mb-1">{t('provider.applicationStatus.devDirectLink')}</p>
                  <a href={magicLinkUrl} className="text-xs text-blue-600 hover:underline break-all" data-testid="magic-link-url">{magicLinkUrl}</a>
                </div>
              )}
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500" data-testid="auto-continue-hint">
                <RefreshCw className="h-4 w-4 animate-spin text-blue-500 flex-shrink-0" />
                <span>{t('provider.applicationStatus.autoContinue')}</span>
              </div>
              <div className="flex items-center justify-center gap-3">
                <Button variant="outline" onClick={handleResend} disabled={isLoading} data-testid="resend-link-btn">
                  <Link2 className="h-4 w-4 mr-1" /> {t('provider.applicationStatus.resend')}
                </Button>
                <Button variant="ghost" onClick={() => { stopPolling(); setPhase('email'); setMagicLinkUrl(null); }} data-testid="back-to-email-btn">
                  {t('provider.applicationStatus.useDifferentEmail')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Phase 3: Authenticated */}
        {phase === 'authenticated' && application && (
          <Card className="shadow-xl border-2 border-blue-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl" data-testid="clinic-name">{application.clinicName}</CardTitle>
                <Badge className={getStatusMeta(application.status).badgeClass} data-testid="status-badge">
                  {t(getStatusMeta(application.status).labelKey)}
                </Badge>
              </div>
              <CardDescription>{application.email}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status Banner */}
              {(() => {
                const s = getStatusMeta(application.status);
                const Icon = s.icon;
                const bannerKey = ({
                  submitted: 'provider.applicationStatus.bannerSubmitted',
                  pending: 'provider.applicationStatus.bannerSubmitted',
                  under_review: 'provider.applicationStatus.bannerUnderReview',
                  approved: 'provider.applicationStatus.bannerApproved',
                  rejected: 'provider.applicationStatus.bannerRejected',
                  info_requested: 'provider.applicationStatus.bannerInfoRequested',
                  more_info_requested: 'provider.applicationStatus.bannerInfoRequested',
                  resubmitted: 'provider.applicationStatus.bannerResubmitted',
                  reapplied: 'provider.applicationStatus.bannerResubmitted',
                })[application.status];
                return (
                  <div className={`flex items-center gap-3 p-4 rounded-lg ${s.bgColor}`} data-testid="status-banner">
                    <Icon className={`h-8 w-8 ${s.color}`} />
                    <div>
                      <p className="font-semibold text-gray-900">
                        {bannerKey ? t(bannerKey) : null}
                      </p>
                      <p className="text-sm text-gray-600">
                        {t('provider.applicationStatus.submittedOn', { date: formatDateLocale(application.submittedAt) })}
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Rejection */}
              {application.status === 'rejected' && application.rejectionReason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-red-800 mb-1">{t('provider.applicationStatus.reasonLabel')}</p>
                  {/* reviewer-authored — not translated */}
                  <p className="text-sm text-red-700">{application.rejectionReason}</p>
                  {application.flaggedFields?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-red-200">
                      <p className="text-xs font-medium text-red-700 mb-1">{t('provider.applicationStatus.fieldsToCorrect')}</p>
                      <div className="flex flex-wrap gap-1">
                        {application.flaggedFields.map(f => (
                          <Badge key={f} variant="outline" className="text-xs bg-red-100 text-red-700 border-red-300">
                            {fieldLabel(f)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Resubmitted */}
              {application.status === 'resubmitted' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-purple-800 mb-1">{t('provider.applicationStatus.resubmissionReceived')}</p>
                  <p className="text-sm text-purple-700">
                    {t('provider.applicationStatus.resubmissionReviewing')}
                    {application.reapplyCount > 1 && t('provider.applicationStatus.resubmissionNumber', { n: application.reapplyCount })}
                  </p>
                </div>
              )}

              {/* Admin info request */}
              {(application.status === 'info_requested' || application.status === 'more_info_requested') && application.additionalInfoRequest && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="info-request-note">
                  <p className="text-sm font-medium text-blue-800 mb-1">{t('provider.applicationStatus.infoRequestedByAdmin')}</p>
                  {/* admin-authored — not translated */}
                  <p className="text-sm text-blue-700 whitespace-pre-wrap">{application.additionalInfoRequest}</p>
                  {application.requestedAt && (
                    <p className="text-xs text-blue-500 mt-2">{t('provider.applicationStatus.requestedOn', { date: formatDateLocale(application.requestedAt) })}</p>
                  )}
                </div>
              )}

              {/* Requirements */}
              {application.requirements?.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-testid="requirements-list">
                  <h4 className="text-sm font-medium text-gray-800 mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-gray-600" />
                    {t('provider.applicationStatus.documentRequirements')}
                  </h4>
                  <div className="space-y-3">
                    {application.requirements.map((req, rIdx) => {
                      const statusColors = {
                        approved: 'border-green-200 bg-green-50/50',
                        verified: 'border-green-200 bg-green-50/50',
                        rejected: 'border-red-200 bg-red-50/50',
                        uploaded: 'border-gray-200 bg-white',
                        pending_review: 'border-gray-200 bg-white',
                        completed: 'border-green-200 bg-green-50/50',
                        partial: 'border-amber-200 bg-amber-50/50',
                        expired: 'border-red-200 bg-red-50/50',
                        requested: 'border-blue-200 bg-blue-50/50',
                        pending: 'border-gray-200 bg-white',
                      };
                      const statusBadgeColors = {
                        approved: 'bg-green-50 text-green-700 border-green-200',
                        verified: 'bg-green-50 text-green-700 border-green-200',
                        rejected: 'bg-red-50 text-red-700 border-red-200',
                        uploaded: 'bg-gray-50 text-gray-600 border-gray-200',
                        pending_review: 'bg-gray-50 text-gray-600 border-gray-200',
                        completed: 'bg-green-50 text-green-700 border-green-200',
                        partial: 'bg-amber-50 text-amber-700 border-amber-200',
                        expired: 'bg-red-50 text-red-700 border-red-200',
                        requested: 'bg-blue-50 text-blue-700 border-blue-200',
                        pending: 'bg-gray-50 text-gray-600 border-gray-200',
                      };
                      const isAdminRequest = req.requirement_origin === 'admin_request';
                      const reqStatusKey = REQ_STATUS_KEYS[req.requirement_status];
                      return (
                        <div key={req.id || rIdx} className={`border rounded-lg p-3 ${statusColors[req.requirement_status] || statusColors[req.status] || statusColors.pending}`}
                          data-testid={`clinic-req-${rIdx}`}>
                          <div className="flex items-center justify-between mb-1">
                            <div>
                              <p className="text-sm font-medium text-gray-800">
                                {/* document_type_name is admin-authored — not translated */}
                                {req.document_type_name}
                                {req.document_type_category && (
                                  <span className="text-xs text-gray-400 ml-1">({req.document_type_category})</span>
                                )}
                              </p>
                              <p className="text-xs text-gray-500 capitalize">
                                {/* purpose is a backend enum — kept canonical */}
                                {req.purpose}
                                {req.is_mandatory_for_approval && <span className="text-red-600 ml-1">{t('provider.applicationStatus.mandatory')}</span>}
                                {isAdminRequest && <span className="text-blue-600 ml-1">{t('provider.applicationStatus.adminRequested')}</span>}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">{req.uploaded_count}/{req.required_count}</span>
                              <Badge variant="outline" className={`text-xs ${statusBadgeColors[req.requirement_status] || statusBadgeColors[req.status] || statusBadgeColors.pending}`}
                                data-testid={`clinic-req-status-${rIdx}`}>
                                {reqStatusKey ? t(reqStatusKey) : req.status}
                              </Badge>
                              {req.expiry_required && <Badge variant="outline" className="text-xs bg-amber-50 text-amber-600 border-amber-200">{t('provider.applicationStatus.expiryRequired')}</Badge>}
                            </div>
                          </div>
                          {req.requirement_status === 'rejected' && (
                            <div className="mt-2 mb-2 rounded-md border border-red-200 bg-red-50 p-2" data-testid={`clinic-req-rejection-${rIdx}`}>
                              <p className="text-xs font-semibold text-red-800">{t('provider.applicationStatus.adminRejectedDoc')}</p>
                              {req.latest_review_comment && (
                                <p className="text-xs text-red-700 mt-0.5 whitespace-pre-wrap">
                                  {t('provider.applicationStatus.adminComment', { text: req.latest_review_comment })}
                                </p>
                              )}
                            </div>
                          )}
                          {req.documents?.length > 0 && (
                            <div className="space-y-1 ml-2 mt-2">
                              {req.documents.map((doc, idx) => (
                                <div key={doc.document_id || idx} className="flex items-center gap-2 text-sm">
                                  <FileText className={`h-3 w-3 flex-shrink-0 ${doc.file_exists === false ? 'text-red-400' : 'text-blue-500'}`} />
                                  {doc.file_exists === false ? (
                                    <span className="text-red-600 truncate text-xs">{doc.file_name} {t('provider.applicationStatus.missing')}</span>
                                  ) : doc.file_url ? (
                                    <a href={doc.file_url.startsWith('/api/') ? `${API}${doc.file_url}` : doc.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate text-xs" data-testid={`clinic-req-doc-link-${rIdx}-${idx}`}>
                                      {doc.file_name}
                                    </a>
                                  ) : (
                                    <span className="text-red-600 truncate text-xs">{doc.file_name} {t('provider.applicationStatus.unavailable')}</span>
                                  )}
                                  {doc.expiry_date && (
                                    <span className={`text-xs px-1 rounded ${
                                      new Date(doc.expiry_date) < new Date() ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                    }`}>
                                      {new Date(doc.expiry_date) < new Date() ? t('provider.applicationStatus.expired') : t('provider.applicationStatus.expiryLabel', { date: doc.expiry_date })}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Unmapped documents */}
              {application.unmappedDocuments?.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-testid="unmapped-documents-list">
                  <h4 className="text-sm font-medium text-gray-600 mb-3">{t('provider.applicationStatus.otherDocuments', { n: application.unmappedDocuments.length })}</h4>
                  <div className="space-y-2">
                    {application.unmappedDocuments.map((doc, idx) => (
                      <div key={doc.document_id || idx} className="flex items-center gap-2 bg-white rounded-md p-2 border border-gray-100">
                        <FileText className={`h-4 w-4 flex-shrink-0 ${doc.file_exists === false ? 'text-red-400' : 'text-blue-500'}`} />
                        {doc.file_exists === false ? (
                          <span className="text-sm text-red-600 truncate">{doc.file_name} {t('provider.applicationStatus.missing')}</span>
                        ) : doc.file_url ? (
                          <a href={doc.file_url.startsWith('/api/') ? `${API}${doc.file_url}` : doc.file_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate" data-testid={`doc-link-${idx}`}>
                            {doc.file_name || doc.document_type || t('provider.applicationStatus.documentFallback')}
                          </a>
                        ) : (
                          <span className="text-sm text-red-600 truncate">{doc.file_name} {t('provider.applicationStatus.unavailable')}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Resubmit Form */}
              {showUploadForm && canReplaceDocuments && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4" data-testid="resubmit-form">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    {t('provider.applicationStatus.provideAdditionalInfo')}
                  </h4>

                  {application.requirements?.filter(r => !['completed','verified','approved'].includes(r.requirement_status || r.status)).length > 0 ? (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">{t('provider.applicationStatus.uploadUpdatedFor')}</p>
                      {application.requirements.filter(r => !['completed','verified','approved'].includes(r.requirement_status || r.status)).map((req, rIdx) => {
                        const statusBadgeColors = {
                          expired: 'bg-red-100 text-red-700',
                          requested: 'bg-blue-100 text-blue-700',
                          partial: 'bg-amber-100 text-amber-700',
                          pending: 'bg-gray-100 text-gray-600',
                        };
                        const needsExpiry = req.expiry_required || req.document_type_requires_expiry;
                        const reqFiles = uploadFiles.filter(f => f._reqSnapId === req.id);
                        const reqStatusKey = REQ_STATUS_KEYS[req.requirement_status];
                        return (
                          <div key={req.id} className="border rounded-lg p-3 bg-white space-y-2" data-testid={`resubmit-req-card-${rIdx}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-blue-500" />
                                <span className="text-sm font-medium">{req.document_type_name}</span>
                                <Badge variant="outline" className={`text-xs ${statusBadgeColors[req.status] || statusBadgeColors.pending}`}>
                                  {reqStatusKey ? t(reqStatusKey) : (req.requirement_status || req.status)}
                                </Badge>
                                {req.is_mandatory_for_approval && <Badge className="bg-red-50 text-red-600 text-xs">{t('provider.applicationStatus.mandatory').replace(/[()]/g, '')}</Badge>}
                                {req.requirement_origin === 'admin_request' && <Badge className="bg-blue-50 text-blue-600 text-xs">{t('provider.applicationStatus.adminRequested').replace(/[()]/g, '')}</Badge>}
                              </div>
                              <span className="text-xs text-gray-500">{req.uploaded_count}/{req.required_count}</span>
                            </div>

                            {req.requirement_status === 'rejected' && req.latest_review_comment && (
                              <div className="rounded-md border border-red-200 bg-red-50 p-2" data-testid={`resubmit-rejection-reason-${rIdx}`}>
                                <p className="text-xs font-semibold text-red-800">{t('provider.applicationStatus.adminRejectedReupload')}</p>
                                <p className="text-xs text-red-700 mt-0.5 whitespace-pre-wrap">{t('provider.applicationStatus.adminComment', { text: req.latest_review_comment })}</p>
                              </div>
                            )}

                            {req.documents?.length > 0 && (
                              <div className="pl-4 space-y-1">
                                {req.documents.map((doc, dIdx) => (
                                  <div key={doc.document_id || dIdx} className="flex items-center gap-2 text-xs">
                                    <FileText className={`h-3 w-3 ${doc.superseded ? 'text-gray-400' : 'text-blue-500'}`} />
                                    <span className={doc.superseded ? 'line-through text-gray-400' : 'text-gray-700'}>{doc.file_name}</span>
                                    {doc.expiry_date && (
                                      <span className={`px-1 rounded ${
                                        new Date(doc.expiry_date) < new Date() ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                      }`}>
                                        {new Date(doc.expiry_date) < new Date() ? t('provider.applicationStatus.expired') : t('provider.applicationStatus.expiryLabel', { date: doc.expiry_date })}
                                      </span>
                                    )}
                                    {doc.superseded && <span className="text-gray-400 italic">{t('provider.applicationStatus.supersededTag')}</span>}
                                  </div>
                                ))}
                              </div>
                            )}

                            {reqFiles.length > 0 && (
                              <div className="space-y-1">
                                {reqFiles.map((f, fIdx) => (
                                  <div key={fIdx} className="flex items-center gap-2 bg-blue-50 p-2 rounded text-xs">
                                    <CheckCircle className="h-3 w-3 text-blue-600" />
                                    <span className="text-blue-900">{f.name} ({t('provider.applicationStatus.kbLabel', { n: (f.size / 1024).toFixed(1) })})</span>
                                    {needsExpiry && (
                                      <DateField
                                        value={f._expiryDate || ''}
                                        onChange={(iso) => {
                                          setUploadFiles(prev => prev.map(pf =>
                                            pf === f ? Object.assign(pf, { _expiryDate: iso }) : pf
                                          ));
                                        }}
                                        className="border rounded px-2 py-1 text-xs w-36"
                                        data-testid={`resubmit-expiry-${rIdx}-${fIdx}`}
                                      />
                                    )}
                                    <button type="button" onClick={() => setUploadFiles(prev => prev.filter(pf => pf !== f))}
                                      className="text-red-400 hover:text-red-600 ml-auto">
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            <label className="flex items-center gap-2 p-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                              <Upload className="h-4 w-4 text-gray-400" />
                              <span className="text-xs text-gray-600">
                                {t('provider.applicationStatus.uploadTypePrefix', { name: req.document_type_name })}
                                {needsExpiry && <span className="text-amber-600 ml-1">{t('provider.applicationStatus.expiryDateRequiredInline')}</span>}
                              </span>
                              <input type="file" multiple className="hidden"
                                onChange={(e) => {
                                  const newFiles = Array.from(e.target.files);
                                  const enrichedFiles = newFiles.map(f => {
                                    f._docTypeId = req.document_type_id;
                                    f._reqSnapId = req.id;
                                    f._expiryDate = '';
                                    f._needsExpiry = needsExpiry;
                                    return f;
                                  });
                                  setUploadFiles(prev => [...prev, ...enrichedFiles]);
                                  e.target.value = '';
                                }}
                                data-testid={`resubmit-upload-${rIdx}`}
                              />
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('provider.applicationStatus.uploadDocuments')}</label>
                      <input type="file" multiple
                        onChange={(e) => {
                          const newFiles = Array.from(e.target.files);
                          setUploadFiles(prev => [...prev, ...newFiles]);
                          e.target.value = '';
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        data-testid="file-upload-input" />
                    </div>
                  )}

                  {uploadFiles.length > 0 && (
                    <div className="border-t pt-3">
                      <p className="text-xs text-gray-500 mb-2">{t('provider.applicationStatus.filesSelected', { n: uploadFiles.length })}</p>
                      <Button
                        size="sm"
                        onClick={handleFileUpload}
                        disabled={isUploading}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="upload-docs-btn"
                      >
                        {isUploading ? (
                          <><RefreshCw className="h-3 w-3 animate-spin mr-1" /> {t('provider.applicationStatus.uploading')}</>
                        ) : (
                          <><Upload className="h-3 w-3 mr-1" /> {t('provider.applicationStatus.uploadAll')}</>
                        )}
                      </Button>
                    </div>
                  )}

                  <textarea
                    value={resubmitNote}
                    onChange={(e) => setResubmitNote(e.target.value)}
                    placeholder={t('provider.applicationStatus.resubmitPlaceholder')}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="resubmit-note-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleResubmit}
                      disabled={isResubmitting}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="resubmit-btn"
                    >
                      {isResubmitting ? (
                        <span className="flex items-center gap-2"><RefreshCw className="h-4 w-4 animate-spin" /> {t('provider.applicationStatus.resubmitting')}</span>
                      ) : (
                        <span className="flex items-center gap-2"><Send className="h-4 w-4" /> {t('provider.applicationStatus.resubmitApp')}</span>
                      )}
                    </Button>
                    <Button variant="outline" onClick={() => setShowUploadForm(false)}>{t('provider.applicationStatus.cancel')}</Button>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="pt-2 space-y-3">
                {application.status === 'approved' && (
                  <Button onClick={handleContinue} className="w-full bg-green-600 hover:bg-green-700" data-testid="continue-approved-btn">
                    {t('provider.applicationStatus.continueToLogin')} <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
                {application.status === 'rejected' && (
                  <Button onClick={handleReapply} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="reapply-btn">
                    <FileText className="h-4 w-4 mr-2" /> {t('provider.applicationStatus.reapplyUpdated')}
                  </Button>
                )}
                {canReplaceDocuments && !showUploadForm && (
                  <Button onClick={handleContinue} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="provide-info-btn">
                    <Upload className="h-4 w-4 mr-2" /> {t('provider.applicationStatus.provideInfoCta')} <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
                {(application.status === 'submitted' || application.status === 'pending') && (
                  <p className="text-center text-sm text-gray-500">{t('provider.applicationStatus.notifySubmitted')}</p>
                )}
                {application.status === 'under_review' && (
                  <p className="text-center text-sm text-gray-500">{t('provider.applicationStatus.notifyUnderReview')}</p>
                )}
                {application.status === 'resubmitted' && (
                  <p className="text-center text-sm text-gray-500">{t('provider.applicationStatus.notifyResubmitted')}</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Links */}
        <div className="mt-8 text-center space-y-3">
          <p className="text-gray-600">
            {t('provider.applicationStatus.noAccountPrefix')}{' '}
            <a href="#/provider/register" className="text-blue-600 hover:underline font-medium">{t('provider.applicationStatus.registerHere')}</a>
          </p>
          <p className="text-gray-600">
            {t('provider.applicationStatus.alreadyPrefix')}{' '}
            <a href="#/login" className="text-blue-600 hover:underline font-medium">{t('provider.applicationStatus.loginClinic')}</a>
          </p>
        </div>
      </div>
    </div>
  );
};

const ApplicationStatusCheckPage = () => (
  <ClinicApplicationProvider>
    <ApplicationStatusCheckContent />
  </ClinicApplicationProvider>
);

export default ApplicationStatusCheckPage;